import React, { createContext, useContext, useState, useCallback, useEffect, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTokenValidation } from '@/hooks/useTokenValidation';
import { useUserRoles, type AppRole } from '@/hooks/useUserRoles';
import { useProductEntitlements, type ProductEntitlement } from '@/hooks/useProductEntitlements';
import { useProfileSafe } from '@/contexts/UserProfileContext';
import { getProductFromPath, type SubscriptionProduct, PRODUCTS } from '@/config/productConfig';

type ViewMode = 'personal' | 'admin';

interface ProfileSwitcherContextType {
  // View mode (personal vs admin)
  viewMode: ViewMode;
  setViewMode: (mode: ViewMode) => void;
  toggleViewMode: () => void;
  isAdminView: boolean;
  
  // Active product (career_path or career_studio)
  activeProduct: SubscriptionProduct;
  setActiveProduct: (product: SubscriptionProduct) => void;
  canSwitchProducts: boolean;
  
  // Product entitlements
  entitlements: Record<'career_path' | 'career_studio', ProductEntitlement>;
  hasProductAccess: (product: SubscriptionProduct) => boolean;
  isLoadingEntitlements: boolean;
  
  // Roles
  roles: AppRole[];
  activeRole: AppRole | null;
  setActiveRole: (role: AppRole) => void;
  hasElevatedAccess: boolean;
  isSuperAdmin: boolean;
  
  // Product access overlay
  showAccessOverlay: boolean;
  overlayProduct: SubscriptionProduct | null;
  setShowAccessOverlay: (show: boolean, product?: SubscriptionProduct) => void;
  
  // Navigation helpers
  navigateToProduct: (product: SubscriptionProduct) => void;
  navigateToAdmin: () => void;
  navigateToPersonal: () => void;
}

const ProfileSwitcherContext = createContext<ProfileSwitcherContextType | null>(null);

export const ProfileSwitcherProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { result } = useTokenValidation();
  const { profile } = useProfileSafe();
  const userId = profile?.id;
  
  // Fetch roles - pass userId if available
  const { roles, activeRole: userActiveRole, setActiveRole: setUserActiveRole, hasElevatedAccess: rolesElevatedAccess, isSuperAdmin } = useUserRoles(userId);
  const { entitlements, hasProduct, isLoading: isLoadingEntitlements, canAccessProduct } = useProductEntitlements();
  
  // hasElevatedAccess should be true if either:
  // 1. User has elevated role from database (rolesElevatedAccess)
  // 2. Token validation says they're admin (result?.isAdmin)
  const hasElevatedAccess = rolesElevatedAccess || result?.isAdmin === true;
  
  // View mode state
  const [viewMode, setViewModeState] = useState<ViewMode>(() => {
    if (typeof window !== 'undefined') {
      return (localStorage.getItem('sirppi_view_mode') as ViewMode) || 'personal';
    }
    return 'personal';
  });
  
  // Active product state
  const [activeProduct, setActiveProductState] = useState<SubscriptionProduct>(() => {
    if (typeof window !== 'undefined') {
      return (localStorage.getItem('sirppi_active_product') as SubscriptionProduct) || 'career_path';
    }
    return 'career_path';
  });
  
  // Access overlay state
  const [showAccessOverlay, setShowAccessOverlayState] = useState(false);
  const [overlayProduct, setOverlayProduct] = useState<SubscriptionProduct | null>(null);

  // Persist view mode
  useEffect(() => {
    localStorage.setItem('sirppi_view_mode', viewMode);
  }, [viewMode]);

  // Persist active product
  useEffect(() => {
    localStorage.setItem('sirppi_active_product', activeProduct);
  }, [activeProduct]);

  // Guests = unauthenticated visitors. Both /career-hub and /studio are public,
  // so guests should be able to freely switch between them with no upsell overlay.
  const isGuest = result?.isValid !== true;

  // Auto-detect product from URL
  useEffect(() => {
    const detectedProduct = getProductFromPath(location.pathname);
    if (detectedProduct && detectedProduct !== activeProduct) {
      // Guests: always allow auto-switch, no overlay.
      if (isGuest) {
        setActiveProductState(detectedProduct);
        return;
      }
      // Check if user has access before auto-switching
      if (hasProduct(detectedProduct) || result?.isAdmin) {
        setActiveProductState(detectedProduct);
      } else if (!isLoadingEntitlements) {
        // Show overlay if user doesn't have access
        setShowAccessOverlayState(true);
        setOverlayProduct(detectedProduct);
      }
    }
  }, [location.pathname, activeProduct, hasProduct, result?.isAdmin, isLoadingEntitlements, isGuest]);

  // Check if user can switch products (has both, is admin, or is guest)
  const canSwitchProducts = useMemo(() => {
    if (isGuest) return true;
    if (result?.isAdmin) return true;
    return hasProduct('career_path') && hasProduct('career_studio');
  }, [hasProduct, result?.isAdmin, isGuest]);

  const setViewMode = useCallback((mode: ViewMode) => {
    setViewModeState(mode);
    if (mode === 'admin') {
      navigate('/admin');
    } else {
      // Navigate to the active product's dashboard
      const productConfig = PRODUCTS[activeProduct];
      navigate(productConfig.dashboardRoute);
    }
  }, [navigate, activeProduct]);

  const toggleViewMode = useCallback(() => {
    const newMode = viewMode === 'personal' ? 'admin' : 'personal';
    setViewMode(newMode);
  }, [viewMode, setViewMode]);

  const setActiveProduct = useCallback((product: SubscriptionProduct) => {
    // Guests can freely browse both products — skip the overlay entirely.
    // Authenticated free-tier users still see the upsell overlay for locked products.
    if (!isGuest && !hasProduct(product) && !result?.isAdmin) {
      setShowAccessOverlayState(true);
      setOverlayProduct(product);
      return;
    }

    setActiveProductState(product);
    const productConfig = PRODUCTS[product];
    navigate(productConfig.dashboardRoute);
  }, [hasProduct, result?.isAdmin, navigate, isGuest]);

  const hasProductAccess = useCallback((product: SubscriptionProduct): boolean => {
    if (result?.isAdmin) return true;
    return hasProduct(product);
  }, [hasProduct, result?.isAdmin]);

  const setShowAccessOverlay = useCallback((show: boolean, product?: SubscriptionProduct) => {
    setShowAccessOverlayState(show);
    if (product) setOverlayProduct(product);
    if (!show) setOverlayProduct(null);
  }, []);

  const navigateToProduct = useCallback((product: SubscriptionProduct) => {
    setActiveProduct(product);
  }, [setActiveProduct]);

  const navigateToAdmin = useCallback(() => {
    setViewMode('admin');
  }, [setViewMode]);

  const navigateToPersonal = useCallback(() => {
    setViewMode('personal');
  }, [setViewMode]);

  const value: ProfileSwitcherContextType = {
    viewMode,
    setViewMode,
    toggleViewMode,
    isAdminView: viewMode === 'admin',
    
    activeProduct,
    setActiveProduct,
    canSwitchProducts,
    
    entitlements,
    hasProductAccess,
    isLoadingEntitlements,
    
    roles,
    activeRole: userActiveRole,
    setActiveRole: setUserActiveRole,
    hasElevatedAccess,
    isSuperAdmin,
    
    showAccessOverlay,
    overlayProduct,
    setShowAccessOverlay,
    
    navigateToProduct,
    navigateToAdmin,
    navigateToPersonal,
  };

  return (
    <ProfileSwitcherContext.Provider value={value}>
      {children}
    </ProfileSwitcherContext.Provider>
  );
};

export const useProfileSwitcher = (): ProfileSwitcherContextType => {
  const context = useContext(ProfileSwitcherContext);
  if (!context) {
    throw new Error('useProfileSwitcher must be used within a ProfileSwitcherProvider');
  }
  return context;
};

// Safe version that returns default values if context is not available
export const useProfileSwitcherSafe = (): Partial<ProfileSwitcherContextType> => {
  const context = useContext(ProfileSwitcherContext);
  return context || {};
};
